import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { SubcategoryService } from '../../../../services/subcategory/subcategory.service';
import { SubcategoriesList } from '../../../../pages/admin-dashboard/admin-subcategories/admin-subcategories.model';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';
import {
  buildPageNumbers,
  getEndItem,
  getStartItem,
  LoadState,
} from '../../../../shared/ui/shared-tables/pagination/pagination.utils';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { SubcategoryFiltersComponent } from './subcategory-filters-component/subcategory-filters-component';
import { SubcategoryFilters, DEFAULT_SUBCATEGORY_FILTERS } from './subcategory-filters-component/subcategory-filters-component.model';
import { SubcategoryTableComponent } from './subcategory-table-component/subcategory-table-component';

@Component({
  selector: 'app-admin-subcategories-list',
  imports: [SubcategoryFiltersComponent, SubcategoryTableComponent, Pagination, ConfirmationDialog, SuccessfulNotification],
  templateUrl: './admin-subcategories-list.html',
  styleUrl: './admin-subcategories-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminSubcategoriesList implements OnInit {
  private router = inject(Router);
  private subcategoryService = inject(SubcategoryService);
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);

  isArabic = computed(() => this.locale.startsWith('ar'));

  filters = signal<SubcategoryFilters>({ ...DEFAULT_SUBCATEGORY_FILTERS });
  subcategories = signal<SubcategoriesList[]>([]);
  state = signal<LoadState>('loading');

  currentPage = signal(1);
  itemsPerPage = signal(25);

  totalItems = signal(0);
  totalPages = computed(() => Math.max(1, Math.ceil(this.totalItems() / this.itemsPerPage())));
  startItem = computed(() => getStartItem(this.currentPage(), this.itemsPerPage(), this.totalItems()));
  endItem = computed(() => getEndItem(this.currentPage(), this.itemsPerPage(), this.totalItems()));
  pageNumbers = computed(() => buildPageNumbers(this.currentPage(), this.totalPages()));
  isFiltersApplied = computed(() => {
    const f = this.filters();
    return !!(f.subcategoryTitle || f.createdOnFrom || f.createdOnTo || f.lastUpdatedOnFrom || f.lastUpdatedOnTo);
  });

  subcategoryToDelete = signal<SubcategoriesList | null>(null);
  deleting = signal(false);
  deleteError = signal('');
  showSuccessMessage = signal(false);

  subcategoryToToggle = signal<{ subcategory: SubcategoriesList; activate: boolean } | null>(null);
  toggling = signal(false);
  toggleError = signal('');

  toggleTitle = computed(() => {
    const activate = this.subcategoryToToggle()?.activate;
    if (this.isArabic()) return activate ? 'تفعيل التصنيف الفرعي' : 'إلغاء تفعيل التصنيف الفرعي؟';
    return activate ? 'Activate Subcategory?' : 'Deactivate Subcategory?';
  });
  toggleConfirm = computed(() => {
    const activate = this.subcategoryToToggle()?.activate;
    if (this.isArabic()) return activate ? 'تفعيل' : 'إلغاء التفعيل';
    return activate ? 'Activate' : 'Deactivate';
  });
  toggleDescription = computed(() => {
    const target = this.subcategoryToToggle();
    const name = (this.isArabic() ? target?.subcategory.titleAr : target?.subcategory.titleEn) || '';
    if (this.isArabic()) {
      return `${target?.activate ? 'هل أنت متأكد من تفعيل' : 'هل أنت متأكد من إلغاء تفعيل'} ${name}؟`;
    }
    return `Are you sure you want to ${target?.activate ? 'activate' : 'deactivate'} ${name}?`;
  });

  deleteTitle = $localize`:@@adminCategoriesDeleteTitle:Delete Subcategory?`;
  deleteCancel = $localize`:@@adminProductsDeleteCancel:Cancel`;
  deleteConfirm = $localize`:@@adminProductsDeleteConfirmButton:Delete`;
  deleteSuccess = $localize`:@@adminCategoriesDeleteSuccess:Subcategory deleted successfully!`;
  deleteDescription = computed(() => {
    const subcategory = this.subcategoryToDelete();
    const name = (this.isArabic() ? subcategory?.titleAr : subcategory?.titleEn) || subcategory?.titleEn || '';
    return `${$localize`:@@adminProductsDeleteConfirm:Are you sure you want to delete`} ${name}?`;
  });

  ngOnInit() {
    this.loadSubcategories();
  }

  loadSubcategories() {
    this.state.set('loading');
    const filters = this.filters();

    const subscription = this.subcategoryService
      .getAllSubcategories({
        subcategoryTitle: filters.subcategoryTitle || undefined,
        pageNum: this.currentPage() - 1,
        pageSize: this.itemsPerPage(),
        orderBy: filters.orderBy,
        orderDir: filters.orderDir,
      })
      .subscribe({
        next: (res) => {
          this.subcategories.set(this.filterByDates(res.list ?? [], filters));
          this.totalItems.set(res.total);
          this.state.set('success');
        },
        error: () => this.state.set('error'),
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  applyFilters(filters: SubcategoryFilters) {
    this.filters.set(filters);
    this.currentPage.set(1);
    this.loadSubcategories();
  }

  resetFilters() {
    this.filters.set({ ...DEFAULT_SUBCATEGORY_FILTERS });
    this.currentPage.set(1);
    this.loadSubcategories();
  }

  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadSubcategories();
    }
  }

  onPageSizeChange(size: number) {
    this.itemsPerPage.set(size);
    this.currentPage.set(1);
    this.loadSubcategories();
  }

  onViewSubcategory(id: number) {
    this.router.navigate(['/admin/subcategories/view-subcategory', id]);
  }

  onEditSubcategory(id: number) {
    this.router.navigate(['/admin/subcategories/edit-subcategory', id]);
  }

  openDeleteDialog(id: number) {
    this.deleteError.set('');
    this.subcategoryToDelete.set(this.subcategories().find((c) => c.id === id) ?? null);
  }

  closeDeleteDialog() {
    this.subcategoryToDelete.set(null);
  }

  confirmDelete() {
    const subcategory = this.subcategoryToDelete();
    if (!subcategory) return;

    this.deleting.set(true);
    this.deleteError.set('');
    const subscription = this.subcategoryService
      .deleteSubcategory(subcategory.id)
      .pipe(finalize(() => this.deleting.set(false)))
      .subscribe({
        next: () => {
          this.closeDeleteDialog();
          this.showSuccessMessage.set(true);
          setTimeout(() => this.showSuccessMessage.set(false), 3000);
          if (this.subcategories().length === 1 && this.currentPage() > 1) {
            this.currentPage.update((page) => page - 1);
          }
          this.loadSubcategories();
        },
        error: (error) =>
          this.deleteError.set(
            this.isArabic()
              ? error?.messageAr || 'حدث بعض الخطأ'
              : error?.messageEn || 'Error deleting subcategory. Please try again later.',
          ),
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  openToggleDialog(id: number, activate: boolean) {
    const subcategory = this.subcategories().find((c) => c.id === id);
    if (!subcategory) return;
    this.toggleError.set('');
    this.subcategoryToToggle.set({ subcategory, activate });
  }

  closeToggleDialog() {
    this.subcategoryToToggle.set(null);
  }

  confirmToggle() {
    const target = this.subcategoryToToggle();
    if (!target) return;

    this.toggling.set(true);
    this.toggleError.set('');
    const request = target.activate
      ? this.subcategoryService.activateSubcategory(target.subcategory.id)
      : this.subcategoryService.deactivateSubcategory(target.subcategory.id);
    const subscription = request.pipe(finalize(() => this.toggling.set(false))).subscribe({
      next: () => {
        this.closeToggleDialog();
        this.loadSubcategories();
      },
      error: (error) =>
        this.toggleError.set(
          this.isArabic()
            ? error?.messageAr || 'حدث بعض الخطأ'
            : error?.messageEn || 'Something went wrong. Please try again later.',
        ),
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  /** The API has no date filters for subcategories, so they are applied to the loaded page. */
  private filterByDates(list: SubcategoriesList[], f: SubcategoryFilters): SubcategoriesList[] {
    const startOf = (value: string) => new Date(value);
    const endOf = (value: string) => {
      const date = new Date(value);
      date.setHours(23, 59, 59, 999);
      return date;
    };

    // Subcategories carry only a creation date, which also stands for "last updated".
    return list.filter((c) => {
      if (!c.createdOn) return !(f.createdOnFrom || f.createdOnTo || f.lastUpdatedOnFrom || f.lastUpdatedOnTo);
      const created = new Date(c.createdOn);
      if (f.createdOnFrom && created < startOf(f.createdOnFrom)) return false;
      if (f.createdOnTo && created > endOf(f.createdOnTo)) return false;
      if (f.lastUpdatedOnFrom && created < startOf(f.lastUpdatedOnFrom)) return false;
      if (f.lastUpdatedOnTo && created > endOf(f.lastUpdatedOnTo)) return false;
      return true;
    });
  }
}
